import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, ComponentRef, EventEmitter, inject, Input, PLATFORM_ID, signal, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { LoaderComponent } from '../loader/loader.component';
import { PdfDocument } from './pdf-engine';

interface PdfEngineComponent {
  pdfSrc?: string | Uint8Array;
  currentPage: number;
  loadComplete: EventEmitter<PdfDocument>;
  pageChange: EventEmitter<number>;
}

@Component({
  selector: 'app-document-preview',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    LoaderComponent
  ],
  templateUrl: './document-preview.html',
  styleUrl: './document-preview.scss',
})
export class DocumentPreview {
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild('container', { read: ViewContainerRef }) container!: ViewContainerRef;

  componentRef?: ComponentRef<PdfEngineComponent>;
  private _pdfSrc?: string | Uint8Array;

  totalPages = 0;
  currentPage = 1;
  isMultiPageMode = false;
  isBrowser = signal(false);
  pdfEngine = signal<Type<PdfEngineComponent> | null>(null);

  constructor() {
    const browser = isPlatformBrowser(this.platformId);
    this.isBrowser.set(browser);

    if (browser) {
      this.loadPdfEngine();
    }
  }

  async loadPdfEngine() {
    try {
      const { PdfEngine } = await import('./pdf-engine');
      this.pdfEngine.set(PdfEngine as Type<PdfEngineComponent>);

      this.cdr.detectChanges();

      if (this.container) {
        this.container.clear();
        this.componentRef = this.container.createComponent(PdfEngine as Type<PdfEngineComponent>);

        if (this._pdfSrc) {
          this.componentRef.setInput('pdfSrc', this._pdfSrc);
        }
        this.componentRef.setInput('currentPage', this.currentPage);

        this.setupComponentSubscriptions();
        console.log('PDF Engine Loaded and Subscribed');
      }
    } catch (err) {
      console.error('Failed to load PDF Engine', err);
    }
  }

  private setupComponentSubscriptions() {
    if (!this.componentRef) return;

    const instance = this.componentRef.instance;

    instance.loadComplete?.subscribe((pdf: PdfDocument) => {
      console.log('onLoadComplete triggered', pdf);
      this.onLoadComplete(pdf);
      this.cdr.detectChanges();
    });

    instance.pageChange?.subscribe((page: number) => {
      console.log('onPageChange triggered', page);
      this.onPageChange(page);
    });
  }

  @Input()
  set pdfSrc(value: string | Uint8Array | ArrayBuffer | null | undefined) {
    if (!value) {
      this._pdfSrc = undefined;
      return;
    }

    if (value instanceof ArrayBuffer) {
      this._pdfSrc = new Uint8Array(value);
    } else {
      this._pdfSrc = value;
    }

    console.log('PDF Preview updated');

    if (this.componentRef) {
      this.componentRef.setInput('pdfSrc', this._pdfSrc);
    }
  }

  get pdfSrc(): string | Uint8Array | undefined {
    return this._pdfSrc;
  }

  onLoadComplete(pdf: PdfDocument) {
    this.totalPages = pdf.numPages;
    console.log(`PDF loaded with ${this.totalPages} pages`);
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.componentRef?.setInput('currentPage', this.currentPage);
    this.cdr.detectChanges();
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.componentRef?.setInput('currentPage', this.currentPage);
      this.cdr.detectChanges();
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.componentRef?.setInput('currentPage', this.currentPage);
      this.cdr.detectChanges();
    }
  }
}

// import { CommonModule, isPlatformBrowser } from '@angular/common';
// import { ChangeDetectorRef, Component, ComponentRef, inject, Input, PLATFORM_ID, signal, Type, ViewChild, ViewContainerRef } from '@angular/core';
// import { MatButtonModule } from '@angular/material/button';
// import { MatIconModule } from '@angular/material/icon';
// import { LoaderComponent } from '../loader/loader.component';
// // import { PdfViewerModule } from 'ng2-pdf-viewer';

// @Component({
//   selector: 'app-document-preview',
//   imports: [
//     CommonModule,
//     // PdfViewerModule,
//     MatButtonModule,
//     MatIconModule,
//     LoaderComponent
//   ],
//   templateUrl: './document-preview.html',
//   styleUrl: './document-preview.scss',
// })
// export class DocumentPreview {
//   private platformId = inject(PLATFORM_ID);
//   private cdr = inject(ChangeDetectorRef);

//   @ViewChild('container', { read: ViewContainerRef }) container!: ViewContainerRef;
//   componentRef?: ComponentRef<any>;
//   private _pdfSrc: any = "/assets/files/Sales_Consultant_Listing_Form.pdf";
//   totalPages = 0;
//   currentPage = 1;
//   isMultiPageMode = false;
//   isBrowser = signal(false);
//   pdfComponent = signal<Type<any> | null>(null);
//   pdfModule = signal<any>(null);
//   pdfEngine = signal<Type<any> | null>(null);

//   constructor() {
//     const browser = isPlatformBrowser(this.platformId);
//     this.isBrowser.set(browser);
    
//     if (browser) {
//       this.loadPdfEngine();
//     }
//   }

//   async loadPdfEngine() {
//     try {
//       const { PdfEngine } = await import('./pdf-engine');
//       this.pdfEngine.set(PdfEngine);
      
//       // Wait for ViewChild to be available
//       this.cdr.detectChanges(); 
      
//       if (this.container) {
//         this.container.clear();
//         this.componentRef = this.container.createComponent(PdfEngine);
        
//         // Pass initial inputs
//         this.componentRef.setInput('pdfSrc', this.pdfSrc);
//         this.componentRef.setInput('currentPage', this.currentPage);
        
//         this.setupComponentSubscriptions();
//         console.log('PDF Engine Loaded and Subscribed');
//       }
//     } catch (err) {
//       console.error('Failed to load PDF Engine', err);
//     }
//   }

//   private setupComponentSubscriptions() {
//     if (!this.componentRef) return;

//     const instance = this.componentRef.instance;

//     instance.loadComplete.subscribe((pdf: any) => {
//       console.log('onLoadComplete triggered', pdf);
//       this.onLoadComplete(pdf);
//       this.cdr.detectChanges(); // Vital for the toolbar to appear
//     });

//     instance.pageChange.subscribe((page: number) => {
//       console.log('onPageChange triggered', page);
//       this.onPageChange(page);
//     });
//   }

//   onComponentActivated(instance: any) {
//     console.log('onComponentActivated', instance);
//     if (instance) {
//       // This manually connects the dynamic component's output to your log method
//       if (instance.loadComplete) {
//         console.log('instance.loadComplete');
//         instance.loadComplete.subscribe((pdf: any) => {
//           this.onLoadComplete(pdf);
//           this.cdr.detectChanges(); // Necessary for Zoneless to see the totalPages change
//         });
//       }
//       if (instance.pageChange) {
//         console.log('instance.pageChange');
//         instance.pageChange.subscribe((page: number) => this.onPageChange(page));
//       }
//     }
//   }

//   @Input() 
//   set pdfSrc(value: any) {
//     if (value) {
//       if (value instanceof ArrayBuffer) {
//         this._pdfSrc = new Uint8Array(value);
//       } else {
//         this._pdfSrc = value;
//       }
//       console.log('PDF Preview updated');
//     }
//   }

//   get pdfSrc(): any {
//     return this._pdfSrc;
//   }

//   onLoadComplete(pdf: any) {
//     this.totalPages = pdf.numPages;
//     console.log(`PDF loaded with ${this.totalPages} pages`);
//   }

//   // onPageChange(page: number) {
//   //   this.currentPage = page;
//   // }

//   onPageChange(event: any) {
//     this.currentPage = typeof event === 'number' ? event : event?.page || 1;
//     this.componentRef?.setInput('currentPage', this.currentPage);
//     this.cdr.detectChanges();
//   }

//   nextPage() {
//     if (this.currentPage < this.totalPages) {
//       this.currentPage++;
//       this.componentRef?.setInput('currentPage', this.currentPage);
//       this.cdr.detectChanges();
//     }
//   }

//   prevPage() {
//     if (this.currentPage > 1) {
//       this.currentPage--;
//       this.componentRef?.setInput('currentPage', this.currentPage);
//       this.cdr.detectChanges();
//     }
//   }
// }
